import { html, View } from 'rune-ts';

export type ActionButtonsMode =
  | 'IDLE'
  | 'HOLDING'
  | 'PLACEMENT_SELECTED'
  | 'INSERT_DRAG'
  | 'OPPONENT_TURN';

export type ActionButtonsData = {
  mode: ActionButtonsMode;
  poolHasTiles: boolean;
  deckHasTiles: boolean;
  /** 멀티: 서버 응답 대기 중. true면 모든 버튼 disable + spinner. */
  sendingMove: boolean;
  onClickDraw: () => void;
  onClickPool: () => void;
  onClickConfirm: () => void;
  onClickCancel: () => void;
};

export class ActionButtonsView extends View<ActionButtonsData> {
  override template() {
    const { mode, sendingMove } = this.data;

    if (sendingMove) {
      return html`
        <div class="action-buttons action-buttons-mode grid grid-cols-1 gap-2 px-2 mlandscape:px-0">
          <div
            class="rounded-xl bg-wood-paper ring-1 ring-card-gold/40 text-card-brass text-center py-3 mlandscape:py-2 text-body flex items-center justify-center gap-2"
            role="status"
            aria-live="polite"
          >
            <span class="inline-flex items-center gap-1">
              <span class="inline-block w-1.5 h-1.5 rounded-full bg-card-gold animate-pulse"></span>
              <span class="inline-block w-1.5 h-1.5 rounded-full bg-card-gold animate-pulse" style="animation-delay:0.15s"></span>
              <span class="inline-block w-1.5 h-1.5 rounded-full bg-card-gold animate-pulse" style="animation-delay:0.3s"></span>
            </span>
            <span>전송 중…</span>
          </div>
        </div>
      `;
    }

    if (mode === 'OPPONENT_TURN') {
      return html`<div class="action-buttons action-buttons-mode hidden"></div>`;
    }

    if (mode === 'IDLE') {
      const drawCls = this.data.deckHasTiles
        ? 'bg-card-navy hover:bg-card-deep text-card-gold'
        : 'bg-wood-sand/40 text-wood-coffee/55 cursor-not-allowed';
      const poolCls = this.data.poolHasTiles
        ? 'bg-wood-paper hover:bg-wood-cream text-wood-ink ring-1 ring-wood-sand'
        : 'bg-wood-paper/60 text-wood-coffee/55 ring-1 ring-wood-sand/60 cursor-not-allowed';
      return html`
        <div class="action-buttons action-buttons-mode grid grid-cols-2 gap-px px-2 mlandscape:px-0 rounded-xl overflow-hidden ring-1 ring-wood-sand/60 shadow-sm">
          <button
            type="button"
            data-role="draw"
            class="flex items-center justify-center gap-2 py-3 mlandscape:py-2 text-body mlandscape:text-caption font-bold transition ${drawCls}"
            ${this.data.deckHasTiles ? '' : 'disabled'}
          >
            <span>더미에서 뽑기</span>
          </button>
          <button
            type="button"
            data-role="pool"
            class="flex items-center justify-center py-3 mlandscape:py-2 text-body mlandscape:text-caption font-semibold transition ${poolCls}"
            ${this.data.poolHasTiles ? '' : 'disabled'}
          >
            풀에서 가져오기
          </button>
        </div>
      `;
    }

    if (mode === 'HOLDING' || mode === 'PLACEMENT_SELECTED') {
      const containerCls =
        'action-buttons action-buttons-mode grid grid-cols-12 gap-2 px-2 mlandscape:flex mlandscape:flex-row mlandscape:justify-end mlandscape:items-stretch mlandscape:gap-2 mlandscape:px-0 min-h-[52px] mlandscape:min-h-[44px]';

      // HOLDING: 자리 미선택 상태에선 별도 ActionButtons row를 그리지 않는다.
      // 풀로 버리기는 HoldingCard 내부의 inline ghost 버튼이 처리 (시선 흐름 단축).
      if (mode === 'HOLDING') {
        return html`<div class="action-buttons action-buttons-mode hidden"></div>`;
      }

      // PLACEMENT_SELECTED: 메인 액션 confirm(세로) + 취소.
      // 풀로 버리기는 HOLDING 단계의 HoldingCard inline 버튼이 일관되게 처리.
      const confirmCls =
        'bg-card-gold hover:bg-card-brass text-wood-ink shadow-md shadow-card-gold/30 active:scale-[0.98]';
      return html`
        <div class="${containerCls}">
          <button
            type="button"
            data-role="confirm"
            class="col-span-9 mlandscape:hidden rounded-xl py-3 text-subhead font-bold transition ${confirmCls}"
          >
            ✓ 배치 확정
          </button>
          <button
            type="button"
            data-role="cancel"
            class="col-span-3 mlandscape:flex-none mlandscape:w-16 rounded-xl mlandscape:rounded-2xl py-3 mlandscape:py-2.5 mlandscape:text-body mlandscape:px-2 font-semibold bg-wood-bark hover:bg-wood-coffee text-wood-paper shadow-md shadow-black/20 active:scale-[0.98] transition"
          >
            취소
          </button>
        </div>
      `;
    }

    // INSERT_DRAG 진행 중
    return html`
      <div class="action-buttons action-buttons-mode grid grid-cols-12 gap-2 px-2 mlandscape:px-0">
        <div class="col-span-12 rounded-xl mlandscape:rounded-2xl py-3 mlandscape:py-2.5 mlandscape:text-body font-semibold bg-card-navy text-card-gold ring-1 ring-card-brass/40 text-center">
          ⇲ 타일을 들어 다른 타일 사이에 끼워넣으세요
        </div>
      </div>
    `;
  }

  protected override onMount(): void {
    const root = this.element();
    const bind = (role: string, fn: () => void) => {
      const btn = root.querySelector<HTMLButtonElement>(`[data-role="${role}"]`);
      btn?.addEventListener('click', () => {
        if (!btn.disabled) fn();
      });
    };
    bind('draw', this.data.onClickDraw);
    bind('pool', this.data.onClickPool);
    bind('confirm', this.data.onClickConfirm);
    bind('cancel', this.data.onClickCancel);
  }
}
